import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Field } from 'redux-form';

import Currency from '../Currency';
import Icon from '../icon/Icon';
import Input from '../formInputs/Input';
import Checkbox from '../formInputs/Checkbox';

import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { hasValueAndIsPositive } from '~APP_UTIL/formValidations';
import { ZERO_DOLLAR_VALUE } from '../../util/constants';
import { newDepartmentTypes } from '../../types/allotments';

const lockedAmount = amount =>
  new TranslatedCopy({ id: 'allotments.form.lockedMin.label', values: { amount } }).toString();

export class DepartmentCard extends PureComponent {
  belowMin = value => {
    const { department } = this.props;
    return value < parseFloat(department.lockedAllotment)
      ? this.lockedAmount(
          <Currency
            value={department.lockedAllotment}
            type={department.currencyCode}
            error={department.lockedAllotment < ZERO_DOLLAR_VALUE}
          />
        )
      : undefined;
  };

  aboveCurrTotal = (value, allValues) => {
    const { unallotedFunds } = this.props;
    const currTotal = allValues
      ? Object.keys(allValues).reduce((total, val) => {
          const newVal = allValues[val];
          if (!newVal) return 0;
          return total + parseFloat(newVal);
        }, 0)
      : 0;
    const normalizedValue = parseFloat(value);

    return currTotal + normalizedValue - normalizedValue > parseFloat(unallotedFunds) ? 'test' : undefined;
  };

  isRemainingAllotmentNegative() {
    const { department } = this.props;
    return parseFloat(department.remainingAllotment) < ZERO_DOLLAR_VALUE;
  }

  renderDisplayAllotment = () => {
    const { department } = this.props;
    const { currencyCode, remainingAllotment } = department;
    const currencyClasses = ['department-remaining-value'];
    return (
      <div className='department-remaining ta-md-c'>
        {remainingAllotment && (
          <span className='u-bold d-lg-b' data-desc='allotments__card_remaining'>
            <span className={currencyClasses.join(' ')}>
              <Currency value={remainingAllotment} type={currencyCode} error={this.isRemainingAllotmentNegative()} />
            </span>
            <small className='body-4 d-sm-b'>
              <TranslatedCopy id='global.remaining' />
            </small>
          </span>
        )}
      </div>
    );
  };

  renderEditAllotment = () => {
    const { department } = this.props;
    const { billable, currencyCode, departmentNumber, lockedAllotment, remainingAllotment } = department;
    const isBillable = billable !== 'false';
    return (
      <div className={`edit-field mt-${isBillable ? '3' : '1'}-md`}>
        {this.isRemainingAllotmentNegative() && <Currency value={remainingAllotment} type={currencyCode} error />}
        <Field
          name={`${departmentNumber}-allotmentAmount`}
          component={Input}
          label={<TranslatedCopy id='department.card.starting' />}
          validate={[hasValueAndIsPositive, this.belowMin, this.aboveCurrTotal]}
        />
        {lockedAllotment && lockedAllotment !== ZERO_DOLLAR_VALUE && (
          <div className='balanceDetails text-color-secondary body-4' data-desc='allotments__card_lockedAmount'>
            {lockedAmount(
              <Currency value={lockedAllotment} type={currencyCode} error={lockedAllotment < ZERO_DOLLAR_VALUE} />
            )}
          </div>
        )}
        {isBillable && (
          <Field
            name={`${departmentNumber}-allowBillableOrders`}
            type='checkbox'
            component={Checkbox}
            label={<TranslatedCopy id='department.card.billable' />}
          />
        )}
      </div>
    );
  };

  render() {
    const { department, isEditing, isSelected, onClick } = this.props;
    const { departmentName, lockedAllotment, purchaseOptions, unitAllotments } = department;
    return (
      <div
        className={classNames('card department-card p12-xl prl5-md pt12-md pb6-md p4-sm mb mb4-md mb2-sm', {
          highlighted: isSelected,
          'edit-mode': isEditing
        })}
        onClick={onClick}
        data-desc='allotments__card_allotment'
      >
        <div className='department-header flex-container space-between start pr4-sm'>
          <div className='headline-4 text-uppercase u-bold mb2-sm mt1-sm' data-desc='allotments__card_departmentName'>
            {departmentName}
          </div>
          <div className='flex-container row start'>
            {unitAllotments && unitAllotments.length > 0 && (
              <span
                className='d-sm-ib mr3-md mr3-sm'
                data-desc='allotments__card_unitAllotment'
                title={new TranslatedCopy({ id: 'global.unitAllotment' }).toString()}
              >
                <Icon name='box' alt='unit allotment' isLight={isSelected} />
              </span>
            )}
            {purchaseOptions && purchaseOptions.length > 0 && (
              <span
                className='mr2-md mr3-sm body-4'
                data-desc='allotments__card_purchaseOptions'
                title={new TranslatedCopy({ id: 'global.purchaseOptions' }).toString()}
              >
                <Icon name='filled-dollar' alt='purchase options' isLight={isSelected} />
              </span>
            )}
            {parseFloat(lockedAllotment) > 0 && (
              <span
                className='body-4'
                data-desc='allotments__card_locked'
                title={new TranslatedCopy({ id: 'alloments.table.locked.title' }).toString()}
              >
                <Icon name='filled-lock' alt='purchase options' isLight={isSelected} />
              </span>
            )}
          </div>
        </div>
        {isEditing ? this.renderEditAllotment() : this.renderDisplayAllotment()}
      </div>
    );
  }
}

DepartmentCard.propTypes = {
  department: newDepartmentTypes.isRequired,
  isEditing: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  unallotedFunds: PropTypes.string
};

DepartmentCard.defaultProps = {
  isEditing: false,
  isSelected: false,
  onClick: null,
  unallotedFunds: '0.00'
};

export default DepartmentCard;
